/**
 * @file: The save of the interval panel.
 *
 * The state manager owns the panel. This class writes the draft of the panel
 * to the server, and it changes the panel when the answer comes. The write and
 * its answer change for reasons of their own, so they are not in the state
 * manager.
 *
 * The state manager owns one instance.
 */

import moment from 'moment'

import { combineDateAndTime } from 'common/date-time'
import { sameIntervalId } from '../calendar-intervals'
import { describeSaveError } from '../save-error'
import { saveProblem } from '../panel-draft'

export class PanelSave {
  constructor (stateManager, SceneCollectionService) {
    this.stateManager = stateManager
    this.controller = stateManager.controller
    this.SceneCollectionService = SceneCollectionService
  }

  /**
     * The one save path. It writes the draft of the panel.
     *
     * The times, the playlist and the role can each change which intervals
     * overlap. The code therefore writes and validates the whole row at once.
     * The transformer works in the same way.
     *
     * @param {Object} [request]
     * @param {boolean} [request.winsPeers] - True when the panel promises the shared stretch
     */
  saveInspector ({ winsPeers } = {}) {
    const panel = this.stateManager.panel

    if (!panel || panel.saving) {
      return
    }

    const source = panel.draft

    // The panel shows the same reason and disables Save.
    if (saveProblem(source, panel.standing)) {
      return
    }

    const startDateTime = combineDateAndTime(source.startDate, source.startTime)
    const endDateTime = combineDateAndTime(source.endDate, source.endTime)
    const intervalData = {
      sceneCollectionId: parseInt(source.sceneCollectionId, 10),
      interval_start: moment(startDateTime).format(),
      interval_end: moment(endDateTime).format(),
      role: panel.standing.role
    }

    if (source.intervalId) {
      intervalData.id = source.intervalId
    }

    if (source.updatedAt) {
      intervalData.baseUpdatedAt = source.updatedAt
    }

    // The operator asked for this interval to take the shared stretch. The
    // server raises it above its peers, and does not change the peers.
    if (winsPeers) {
      intervalData.winsPeers = true
    }

    panel.saving = true
    this.saveIntervalData(intervalData)
  }

  saveIntervalData (intervalData) {
    const isUpdate = Boolean(intervalData.id)
    const verb = isUpdate ? 'updated' : 'created'
    const shownAtSave = this.stateManager.panel
    const draftAtSave = shownAtSave.draft

    return this.SceneCollectionService.addInterval(intervalData)
      .then(response => {
        this.controller._$log.info(`[ScheduleCalendar] Interval ${verb} successfully:`, response)

        this.showSavedTimes(intervalData.interval_start)
        shownAtSave.saving = false

        // The operator can open another interval before the answer comes, and
        // a drag of the bar can change the draft. The panel then shows work
        // that this save did not write, so it stays open.
        if (this.stateManager.panel === shownAtSave && shownAtSave.draft === draftAtSave) {
          this.stateManager.clearInspector()
        }
      })
      .catch(error => {
        this.controller._$log.error(`[ScheduleCalendar] Failed to save interval (${verb}):`, error)
        shownAtSave.saving = false

        // The server finds conflicts that the calendar cannot see. These are
        // intervals outside the fetched window, and collections that a second
        // scene shares. An open panel keeps its draft and its baseline, so the
        // edits stay unsaved. The panel can show another interval now, and it
        // then stays on that interval.
        //
        // The operator can close the panel before the answer comes. The panel
        // then opens again on the draft, so the work is not lost.
        if (!this.stateManager.panel && shownAtSave && !this.controller.pageManager.hasLeft) {
          this.stateManager.setInspecting(shownAtSave.baseline, shownAtSave.draft)
        }

        this.controller._toaster.error(describeSaveError(error), 'Could not save the interval')
      })
  }

  /** True while the open panel waits for the answer to a save of this interval. */
  isSavingInterval (intervalId) {
    const panel = this.stateManager.panel

    return Boolean(panel && panel.saving && sameIntervalId(panel.draft.intervalId, intervalId))
  }

  /**
   * Unlock a panel whose save gets no answer.
   *
   * The API has no timeout. The answer to a request on the old connection
   * does not come after a reconnect, so the panel stays read-only. The
   * reconnect also fetches the week, and the grid then shows if the row was
   * written. A second save of a new interval can write a second row, so the
   * operator gets a warning.
   */
  followReconnect () {
    const panel = this.stateManager.panel

    if (!panel || !panel.saving) {
      return
    }

    panel.saving = false
    this.controller._toaster.warning(
      'The connection dropped during the save. Check the grid before you save again.',
      'The save may not have landed')
  }

  /**
   * Show the grid with the bar of a save. The saved interval can be in
   * another week. The grid then goes there, and that move fetches the week.
   * A refetch of the week on show reads the saved row in the other case.
   */
  showSavedTimes (start) {
    const calendarManager = this.controller.calendarManager

    if (!calendarManager) {
      return
    }

    if (calendarManager.mustMoveToShow(start)) {
      calendarManager.showTime(start)
    } else {
      calendarManager.scheduleRefetch()
    }
  }
}
